import React, { useCallback, useEffect, useState } from "react";
import { getAllCallLogs } from "../CallLogs/components/ShowAll/ShowAllService";
import RecordingCell from "../CallLogs/components/ShowAll/RecordingCell";
import {
  formatCallDuration,
  statusBadgeClass,
} from "../CallLogs/callLogHelpers";
import YhSelect from "../../ui/YhSelect";

type Props = {
  /** National / E.164 phone digits used to match call_logs */
  phone?: string | null;
  title?: string;
  subtitle?: string;
};

const STATUS_FILTERS = [
  { label: "All calls", value: "all" },
  { label: "Answered", value: "answered" },
  { label: "Missed / failed", value: "missed" },
  { label: "With recording", value: "recording" },
];

const Empty = () => <span className="text-[#9CA3AF]">—</span>;

/**
 * Call history for a lead or customer — filtered by phone number.
 */
export default function EntityCallHistory({
  phone,
  title = "Calls",
  subtitle = "IVR / click-to-call history for this contact.",
}: Props) {
  const [rows, setRows] = useState<any[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    answered: 0,
    missed: 0,
    withRecording: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  const digits = String(phone || "").replace(/\D/g, "");
  const national = digits.slice(-10);

  const load = useCallback(async () => {
    if (!national || national.length < 8) {
      setRows([]);
      setStats({ total: 0, answered: 0, missed: 0, withRecording: 0 });
      setTotal(0);
      return;
    }
    try {
      setLoading(true);
      const res = await getAllCallLogs({
        pageNumber: page,
        pageSize,
        phone: national,
        status: statusFilter === "all" ? "" : statusFilter,
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setTotal(res?.metaData?.[0]?.total || list.length || 0);
      if (res?.stats) {
        setStats({
          total: res.stats.total || 0,
          answered: res.stats.answered || 0,
          missed: res.stats.missed || 0,
          withRecording: res.stats.withRecording || 0,
        });
      }
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [national, page, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  if (!national || national.length < 8) {
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-[#111827]">{title}</h2>
          <p className="text-sm text-[#6B7280]">{subtitle}</p>
        </div>
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 py-10 text-center">
          <p className="text-sm font-medium text-[#6B7280]">
            No phone number on this record — calls cannot be matched yet.
          </p>
        </div>
      </div>
    );
  }

  const maxPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#111827]">{title}</h2>
          <p className="text-sm text-[#6B7280]">{subtitle}</p>
        </div>
        <div className="w-full sm:w-[200px]">
          <YhSelect
            placeholder="Filter"
            className="!w-full"
            value={statusFilter}
            options={STATUS_FILTERS}
            onChange={(e) => {
              setStatusFilter(String(e.target.value || "all"));
              setPage(0);
            }}
          />
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          { label: "Total", value: stats.total, tone: "text-[#111827]" },
          { label: "Answered", value: stats.answered, tone: "text-emerald-700" },
          { label: "Missed", value: stats.missed, tone: "text-red-600" },
          {
            label: "Recordings",
            value: stats.withRecording,
            tone: "text-[#841C39]",
          },
        ].map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-[#EEF0F5] bg-[#FAFAFA] px-3 py-2.5"
          >
            <p className="text-[10px] font-medium uppercase tracking-wide text-[#9CA3AF]">
              {s.label}
            </p>
            <p className={`text-lg font-bold tabular-nums ${s.tone}`}>
              {s.value}
            </p>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-[#9CA3AF]">Loading calls…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 py-10 text-center">
          <p className="text-sm font-medium text-[#6B7280]">No calls found</p>
          <p className="mt-1 text-xs text-[#9CA3AF]">
            For ···{national.slice(-4)}. Try an IVR call from this lead first.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#EEF0F5]">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#EEF0F5] bg-[#FAFAFA] text-xs text-[#6B7280]">
                  <th className="px-4 py-2.5 font-medium">Date</th>
                  <th className="px-3 py-2.5 font-medium">Agent</th>
                  <th className="px-3 py-2.5 font-medium">Duration</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Recording</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = String(
                    row.callStatus || row.callAction || ""
                  ).toUpperCase();
                  const when = row.callStartTime
                    ? new Date(row.callStartTime)
                    : null;
                  return (
                    <tr
                      key={row._id || row.uniqueCallId}
                      className="border-b border-[#F3F4F6] last:border-0"
                    >
                      <td className="px-4 py-3 text-xs text-[#374151]">
                        {when ? (
                          <>
                            <div>
                              {when
                                .toLocaleDateString("en-GB")
                                .replace(/\//g, "-")}
                            </div>
                            <div className="text-[#9CA3AF]">
                              {when.toLocaleTimeString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </>
                        ) : (
                          <Empty />
                        )}
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {row.agentName || <Empty />}
                      </td>
                      <td className="px-3 py-3 tabular-nums text-[#374151]">
                        {formatCallDuration(row) || <Empty />}
                      </td>
                      <td className="px-3 py-3">
                        {status ? (
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${statusBadgeClass(
                              status
                            )}`}
                          >
                            {status}
                          </span>
                        ) : (
                          <Empty />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <RecordingCell
                          fileName={row.recordingFileName}
                          recordingUrl={row.recordingUrl}
                          storedRecordingUrl={row.storedRecordingUrl}
                          callLogId={row._id}
                          durationLabel={formatCallDuration(row)}
                          recordingStatus={row.recordingStatus}
                          recordingError={row.recordingError}
                          recordingStoreStatus={row.recordingStoreStatus}
                          recordingStoreError={row.recordingStoreError}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {total > pageSize ? (
            <div className="flex items-center justify-between border-t border-[#EEF0F5] px-4 py-2.5 text-xs text-[#6B7280]">
              <span>
                Page {page + 1} of {maxPage + 1} · {total} calls
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="rounded-lg border border-[#EEF0F5] px-2.5 py-1 font-medium disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  disabled={page >= maxPage}
                  onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
                  className="rounded-lg border border-[#EEF0F5] px-2.5 py-1 font-medium disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
